import { api } from '@/lib/axios'

/**
 * Wie ein fertiges PDF beim Benutzer ankommt — Mandanten-Einstellung.
 *
 * `download` legt die Datei im Download-Ordner ab, mit dem Dateinamen aus der
 * Namenskonvention des Mandanten. `tab` öffnet sie stattdessen in einem neuen
 * Browser-Tab; das ist bequemer zum Ansehen, setzt aber eine Popup-Freigabe der
 * Seite voraus. Ohne diese Freigabe fällt die Ausgabe auf den Download zurück
 * und blendet die Anleitung ein (siehe `PopupBlockedDialog`).
 *
 * XLSX und DOCX werden immer heruntergeladen — sie haben keine Browser-Ansicht.
 *
 * Der Wert wird modulweit gecacht, damit nicht jede Liste einen eigenen Request
 * auslöst; `downloadFile()` liest ihn synchron. Deshalb wird er beim Start des
 * App-Layouts vorgeladen (`primePdfDelivery`). Ist er noch nicht da, gilt der
 * sichere Standard: herunterladen.
 */
export type PdfDelivery = 'download' | 'tab'

const DEFAULT_DELIVERY: PdfDelivery = 'download'

let cached: PdfDelivery | null = null
let inflight: Promise<PdfDelivery> | null = null

/**
 * Der Request geht bewusst direkt über die geteilte Axios-Instanz und nicht über
 * `features/branding` — eine Bibliothek unter `lib/` darf nicht in ein Feature
 * greifen (AP-21-B).
 */
async function load(): Promise<PdfDelivery> {
  if (cached) return cached
  if (!inflight) {
    inflight = api
      .get<{ exportPdfDelivery?: PdfDelivery | null } | null>('/branding')
      .then(({ data }) => {
        cached = data?.exportPdfDelivery === 'tab' ? 'tab' : DEFAULT_DELIVERY
        return cached
      })
      .catch(() => DEFAULT_DELIVERY)
      .finally(() => {
        inflight = null
      })
  }
  return inflight
}

/** Einmal beim Start laden, damit der synchrone Zugriff später etwas vorfindet. */
export function primePdfDelivery(): void {
  void load()
}

/** Synchroner Zugriff für `downloadFile()`; ohne geladenen Wert der Standard. */
export function getPdfDelivery(): PdfDelivery {
  return cached ?? DEFAULT_DELIVERY
}

/** Nach dem Speichern in der Branding-Maske — ohne das gölte bis zum Neuladen der alte Wert. */
export function setPdfDelivery(value: PdfDelivery): void {
  cached = value
}

/**
 * Beim Abmelden leeren: Der Wert gehört dem Mandanten, nicht dem Gerät (KA-11).
 * Sonst sähe der nächste Benutzer auf demselben Rechner die fremde Einstellung.
 */
export function resetPdfDeliveryCache(): void {
  cached = null
  inflight = null
}
